import { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { CreditCard, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';

/**
 * Lazy-load the Razorpay Checkout script exactly once and resolve when
 * `window.Razorpay` is available. Uses a module-level promise so multiple
 * buttons on the same page don't inject the script multiple times.
 */
let _razorpayScriptPromise = null;
export function loadRazorpayScript() {
    if (_razorpayScriptPromise) return _razorpayScriptPromise;
    _razorpayScriptPromise = new Promise((resolve, reject) => {
        if (typeof window !== 'undefined' && window.Razorpay) {
            resolve(window.Razorpay);
            return;
        }
        const s = document.createElement('script');
        s.src = 'https://checkout.razorpay.com/v1/checkout.js';
        s.async = true;
        s.onload = () => {
            if (window.Razorpay) resolve(window.Razorpay);
            else reject(new Error('Razorpay script loaded but window.Razorpay missing'));
        };
        s.onerror = () => reject(new Error('Failed to load Razorpay checkout script'));
        document.body.appendChild(s);
    });
    return _razorpayScriptPromise;
}

/**
 * Reusable Razorpay checkout button.
 *
 * Two modes:
 * - **`flow="app"` (default)** — authenticated flow. Calls `POST /api/razorpay/order`
 *   with `application_id`/`payment_type`/`amount_paise` and opens Checkout.
 *   Requires the caller to be logged in.
 * - **`flow="booking"`** — public landing-page flow. Calls
 *   `POST /api/razorpay/booking-order` with `{name, mobile, email, city, ...}`.
 *   No auth required; auto-creates a lead server-side.
 *
 * Props:
 * @param {'app'|'booking'} flow
 * @param {number}          amountPaise      required — amount in paise
 * @param {string}          [applicationId]  required for `app` flow
 * @param {string}          [paymentType]    e.g. 'booking' | 'first' | 'installment'
 * @param {string}          [description]    shown on Razorpay checkout modal
 * @param {Object}          [bookingPayload] required for `booking` flow — {name, mobile, email, city, interest_kw}
 * @param {Object}          [prefill]        optional: {name, email, contact}
 * @param {string}          [label='Pay Now']
 * @param {string}          [testId='razorpay-pay-btn']
 * @param {string}          [size='default']  Button size
 * @param {string}          [variant='default'] Button variant
 * @param {string}          [className]
 * @param {(res:Object)=>void} [onSuccess]  called after verify() confirms
 * @param {(err:Object)=>void} [onFailure]  called on cancel/failure
 * @param {boolean}         [disabled]
 */
export function RazorpayButton({
    flow = 'app',
    amountPaise,
    applicationId = '',
    paymentType = 'installment',
    description = '',
    bookingPayload = null,
    prefill = null,
    label = 'Pay Now',
    testId = 'razorpay-pay-btn',
    size = 'default',
    variant = 'default',
    className = '',
    onSuccess = null,
    onFailure = null,
    disabled = false,
}) {
    const [busy, setBusy] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [surcharge, setSurcharge] = useState(null);  // preview breakdown

    // Actual Razorpay flow — refactored so we can call it either directly
    // (no surcharge) OR from the confirm-dialog "Proceed to pay" button.
    const runCheckout = useCallback(async () => {
        setBusy(true);
        try {
            // 1) Fetch public config so we know the key_id (and that Razorpay is on).
            const cfg = await api.razorpayConfig();
            if (!cfg?.enabled || !cfg?.key_id) {
                toast.error('Online payments are not enabled. Please contact support.');
                onFailure?.({ code: 'not_enabled' });
                return;
            }

            // 2) Server-side order creation.
            let orderResp;
            if (flow === 'booking') {
                if (!bookingPayload || !bookingPayload.name || !bookingPayload.mobile) {
                    toast.error('Please provide name and mobile');
                    onFailure?.({ code: 'missing_booking_payload' });
                    return;
                }
                orderResp = await api.razorpayBookingOrder({
                    ...bookingPayload,
                    amount_paise: amountPaise,
                });
            } else {
                orderResp = await api.razorpayOrder({
                    amount_paise: amountPaise,
                    application_id: applicationId || undefined,
                    payment_type: paymentType,
                    description,
                });
            }

            // 3) Load Checkout.js on demand.
            const Razorpay = await loadRazorpayScript();

            // 4) Open the modal.
            const rzp = new Razorpay({
                key: cfg.key_id,
                amount: orderResp.amount_paise,
                currency: orderResp.currency || 'INR',
                order_id: orderResp.order_id,
                name: cfg.company_name || 'UPPower Solar',
                description: description || (flow === 'booking' ? '₹1 Booking — PM Surya Ghar' : `Application #${applicationId?.slice(0, 8)}`),
                image: '/logo192.png',
                prefill: prefill || (flow === 'booking'
                    ? { name: bookingPayload?.name, contact: bookingPayload?.mobile, email: bookingPayload?.email }
                    : undefined),
                theme: { color: '#1e3a8a' },
                method: {
                    upi: true,
                    card: true,
                    netbanking: true,
                    wallet: true,
                    emi: false,
                    paylater: false,
                },
                config: {
                    display: {
                        blocks: {
                            upi_block: {
                                name: 'Pay via UPI',
                                instruments: [
                                    { method: 'upi', flows: ['qr', 'intent', 'collect'] },
                                ],
                            },
                        },
                        sequence: ['block.upi_block'],
                        preferences: { show_default_blocks: true },
                    },
                },
                notes: {
                    application_id: applicationId || '',
                    payment_type: paymentType || '',
                    flow,
                },
                handler: async (res) => {
                    try {
                        const v = await api.razorpayVerify({
                            razorpay_order_id: res.razorpay_order_id,
                            razorpay_payment_id: res.razorpay_payment_id,
                            razorpay_signature: res.razorpay_signature,
                        });
                        if (v?.verified) {
                            toast.success('Payment received — thank you!');
                        } else {
                            toast.warning('Payment received. Verification pending — you will see it shortly.');
                        }
                        onSuccess?.({ ...res, ...v });
                    } catch (e) {
                        toast.warning('Payment captured but verification failed. Support has been notified.');
                        onFailure?.({ code: 'verify_failed', error: e });
                    }
                },
                modal: {
                    ondismiss: () => {
                        onFailure?.({ code: 'dismissed' });
                    },
                },
            });
            rzp.on('payment.failed', (evt) => {
                const reason = evt?.error?.description || 'Payment failed';
                toast.error(reason);
                onFailure?.({ code: 'payment_failed', ...evt });
            });
            rzp.open();
        } catch (e) {
            const msg = e?.response?.data?.detail || e?.message || 'Unable to start payment';
            toast.error(msg);
            onFailure?.({ code: 'setup_failed', error: e });
        } finally {
            setBusy(false);
        }
    }, [flow, amountPaise, applicationId, paymentType, description, bookingPayload, prefill, onSuccess, onFailure]);

    // Entry point: for non-booking flows, first fetch surcharge preview. If
    // a fee applies, show breakdown confirmation dialog before starting the
    // real Razorpay order/modal.
    const handleClick = useCallback(async () => {
        if (flow === 'booking') {
            // Landing bookings never carry a surcharge per admin decision.
            return runCheckout();
        }
        setBusy(true);
        try {
            const cfg = await api.razorpayConfig();
            if (!cfg?.surcharge_enabled) {
                setBusy(false);
                return runCheckout();
            }
            const previewFlow = 'customer_application';   // this button is the app flow
            const preview = await api.razorpaySurchargePreview(amountPaise, previewFlow);
            setBusy(false);
            if (!preview?.applies) {
                return runCheckout();
            }
            setSurcharge(preview);
            setConfirmOpen(true);
        } catch (e) {
            setBusy(false);
            // Fee preview failure should not block payment — fall through to normal flow
            return runCheckout();
        }
    }, [flow, amountPaise, runCheckout]);

    const fmtInr = (paise) => `₹${(paise / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    const totalRazorpay = surcharge?.mode === 'addon' ? surcharge?.total_payable_paise : surcharge?.base_paise;

    return (
        <>
            <Button
                size={size}
                variant={variant}
                className={className}
                onClick={handleClick}
                disabled={busy || disabled || !amountPaise || amountPaise < 100}
                data-testid={testId}
            >
                {busy ? (
                    <Loader2 className='w-4 h-4 mr-1.5 animate-spin' />
                ) : (
                    <CreditCard className='w-4 h-4 mr-1.5' />
                )}
                {label}
            </Button>

            <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                <AlertDialogContent data-testid='surcharge-confirm-dialog'>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Payment Breakdown</AlertDialogTitle>
                        <AlertDialogDescription>
                            आपसे नीचे दिखाई गई राशि charge की जाएगी। Please review before proceeding.
                        </AlertDialogDescription>
                    </AlertDialogHeader>

                    {surcharge && (
                        <div className='space-y-2 rounded-md border p-3 bg-muted/30 text-sm'>
                            <div className='flex items-center justify-between'>
                                <span>Base amount</span>
                                <span className='font-mono' data-testid='confirm-base'>{fmtInr(surcharge.base_paise)}</span>
                            </div>
                            <div className='flex items-center justify-between'>
                                <span>
                                    {surcharge.label}
                                    {surcharge.type === 'percentage' ? ` (${surcharge.value}%)` : ' (flat)'}
                                </span>
                                <span className='font-mono' data-testid='confirm-fee'>{fmtInr(surcharge.surcharge_paise)}</span>
                            </div>
                            {surcharge.gst_paise > 0 && (
                                <div className='flex items-center justify-between text-xs opacity-80'>
                                    <span>GST ({surcharge.gst_percent}%) on fee</span>
                                    <span className='font-mono' data-testid='confirm-gst'>{fmtInr(surcharge.gst_paise)}</span>
                                </div>
                            )}
                            <div className='border-t pt-2 flex items-center justify-between font-semibold text-base'>
                                <span>{surcharge.mode === 'addon' ? 'Razorpay Total' : 'Razorpay Total (base)'}</span>
                                <span className='font-mono' data-testid='confirm-total'>{fmtInr(totalRazorpay || 0)}</span>
                            </div>
                            {surcharge.mode === 'wallet_debit' && (
                                <div className='text-[11px] text-amber-700 dark:text-amber-400 mt-1'>
                                    ⓘ अतिरिक्त <b className='font-mono'>{fmtInr(surcharge.total_surcharge_paise)}</b> gateway fee आपके wallet से payment success होते ही auto-deduct होगा।
                                </div>
                            )}
                        </div>
                    )}

                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid='surcharge-confirm-cancel'>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => { setConfirmOpen(false); runCheckout(); }} data-testid='surcharge-confirm-proceed'>
                            Proceed to Pay
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}

export default RazorpayButton;
